import React from 'react';
import { Link } from 'react-router-dom';
import { Factory, Fingerprint, Route as IconoRecorrido, Wrench } from 'lucide-react';
import { medida, rutaArbol } from 'lib/arboles';
import { textoAnios, vidaUtil } from 'lib/cables';
import { fechaCorta, fechaHoraCorta, nombreUsuario } from 'lib/auditoria';
import { etiquetaInstalacion } from 'lib/auditoriaInstalaciones';
import { Dato, Foto, Grupo } from 'pages/arboles/FichaPartes';

const claseEnlace = 'text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary';

// Plataforma con el árbol que la sostiene (enlace a su ficha) si se registró.
function Extremo({ plataforma, arbolId, arbolCodigo }) {
  if (!plataforma && !arbolId) return null;
  return (
    <>
      {plataforma || 'Plataforma sin registrar'}
      {arbolId && (
        <span className="block text-xs font-normal text-muted-foreground">
          Árbol{' '}
          <Link to={rutaArbol(arbolId)} className={claseEnlace}>{arbolCodigo || 'ver ficha'}</Link>
        </span>
      )}
    </>
  );
}

// Datos del cable en cuatro grupos (como la ficha del árbol): identificación,
// recorrido, especificaciones técnicas y proveniencia.
export default function DatosCable({ linea, hoy, nombres, errorFoto }) {
  const vida = vidaUtil(linea, hoy);
  const conFoto = !!linea.foto_path;
  return (
    <div>
      {errorFoto && <p role="alert" className="mb-3 rounded-lg bg-error/10 px-2.5 py-2 text-xs text-error">{errorFoto}</p>}
      <div className={conFoto ? 'grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]' : ''}>
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          <Grupo titulo="Identificación" icono={Fingerprint}>
            <Dato label="Código">{linea.codigo}</Dato>
            <Dato label="Nombre">{linea.nombre}</Dato>
            <Dato label="Instalación">{etiquetaInstalacion(linea.instalacion)}</Dato>
            <Dato label="Registrado">
              {linea.created_at && (
                <>
                  {fechaHoraCorta(linea.created_at)}
                  {linea.created_by && <span className="block text-xs font-normal text-muted-foreground">{nombreUsuario(linea.created_by, nombres)}</span>}
                </>
              )}
            </Dato>
          </Grupo>
          <Grupo titulo="Recorrido" icono={IconoRecorrido}>
            <Dato label="Sale de">
              <Extremo plataforma={linea.plataforma_desde} arbolId={linea.arbol_desde_id} arbolCodigo={linea.arbol_desde_codigo} />
            </Dato>
            <Dato label="Llega a">
              <Extremo plataforma={linea.plataforma_hasta} arbolId={linea.arbol_hasta_id} arbolCodigo={linea.arbol_hasta_codigo} />
            </Dato>
            <Dato label="Longitud" mono>{medida(linea.longitud_m, 'm')}</Dato>
          </Grupo>
          <Grupo titulo="Especificaciones técnicas" icono={Wrench}>
            <Dato label="Diámetro" mono>{medida(linea.diametro_mm, 'mm')}</Dato>
            <Dato label="Material">{linea.material}</Dato>
            <Dato label="Construcción">{linea.construccion}</Dato>
            <Dato label="Carga de rotura" mono>{medida(linea.carga_rotura_kn, 'kN')}</Dato>
            <Dato label="Tensión de diseño">{linea.tension_diseno}</Dato>
            <Dato label="Flecha de diseño">{linea.flecha_diseno}</Dato>
            <Dato label="Freno">{linea.freno}</Dato>
          </Grupo>
          <Grupo titulo="Proveniencia" icono={Factory}>
            <Dato label="Fabricante">{linea.fabricante}</Dato>
            <Dato label="Proveedor">{linea.proveedor}</Dato>
            <Dato label="Fecha de instalación">{linea.fecha_instalacion && fechaCorta(linea.fecha_instalacion)}</Dato>
            <Dato label="Vida útil">{vida.vida !== null ? textoAnios(vida.vida) : null}</Dato>
            <Dato label="Edad">{vida.edad !== null ? textoAnios(vida.edad) : null}</Dato>
          </Grupo>
        </div>
        {conFoto && <Foto path={linea.foto_path} alt="Foto del cable" />}
      </div>
      {linea.notas && (
        <div className="mt-5 border-t border-border pt-4">
          <h3 className="mb-1.5 text-[13px] font-semibold text-primary">Observaciones</h3>
          <p className="max-w-prose whitespace-pre-line text-sm leading-relaxed text-foreground">{linea.notas}</p>
        </div>
      )}
    </div>
  );
}
